/**
 * Provider dello stato di autenticazione.
 *
 * Espone solo l'utente e le azioni: l'access token resta dentro
 * `api-client.ts`, in memoria, e non passa mai dal React state (ADR-0004).
 * Metterlo nello state significherebbe esporlo ai DevTools e a qualunque
 * componente lo legga per sbaglio.
 *
 * All'avvio si tenta un refresh silenzioso: è così che la sessione sopravvive
 * a un reload della pagina senza persistere nulla di sensibile nel browser.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { restoreSession, setAccessToken, setSessionExpiredHandler } from '@/lib/api-client';
import * as authApi from './api';
import type { LoginPayload, RegisterPayload, User } from './api';
import { AuthContext, type AuthContextValue } from './auth-context';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const queryClient = useQueryClient();

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    // La cache contiene dati finanziari dell'utente uscito: va svuotata.
    queryClient.clear();
  }, [queryClient]);

  // Il client HTTP non conosce React: gli passiamo un callback da invocare
  // quando anche il refresh fallisce.
  useEffect(() => {
    setSessionExpiredHandler(clearSession);
    return () => setSessionExpiredHandler(null);
  }, [clearSession]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const restored = await restoreSession();
      if (cancelled) return;

      if (restored) {
        try {
          const me = await authApi.fetchMe();
          if (!cancelled) setUser(me);
        } catch {
          if (!cancelled) clearSession();
        }
      }

      if (!cancelled) setIsInitializing(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const handleLogin = useCallback(async (payload: LoginPayload) => {
    const response = await authApi.login(payload);
    setAccessToken(response.accessToken);
    setUser(response.user);
  }, []);

  const handleRegister = useCallback(async (payload: RegisterPayload) => {
    const response = await authApi.register(payload);
    setAccessToken(response.accessToken);
    setUser(response.user);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // Anche se la chiamata fallisce, lato client la sessione va chiusa.
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isInitializing,
      isAuthenticated: user !== null,
      login: handleLogin,
      register: handleRegister,
      logout: handleLogout,
    }),
    [user, isInitializing, handleLogin, handleRegister, handleLogout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
